<template>
  <div>
    <v-progress-linear
      v-if="loading"
      indeterminate
      class="mb-4"
      color="primary"
    />

    <v-tabs
      v-model="activeTab"
      background-color="transparent"
      color="primary"
      class="mb-6"
    >
      <v-tab>
        <v-icon left>mdi-file-document-edit-outline</v-icon>
        General
      </v-tab>

      <v-tab :disabled="!canUseDiariesTab">
        <v-icon left>mdi-clipboard-text-clock-outline</v-icon>
        Diárias
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab">
      <v-tab-item>
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="fields.name"
                label="Nome da OT *"
                outlined
                dense
                :rules="[requiredRule]"
              />
            </v-col>

            <v-col cols="12" md="3">
              <v-select
                v-model="fields.status"
                :items="statusOptions"
                item-text="text"
                item-value="value"
                label="Estado *"
                outlined
                dense
                :rules="[requiredRule]"
              />
            </v-col>

            <v-col cols="12" md="3">
              <v-text-field
                :value="formattedTotalCost"
                label="Custo total"
                outlined
                dense
                readonly
                disabled
              />
            </v-col>

            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="fields.obra_id"
                :items="obras"
                :item-text="getObraText"
                item-value="Id"
                label="Obra *"
                outlined
                dense
                clearable
                :loading="loadingObras"
                :rules="[requiredRule]"
                @change="changeWork"
              />
            </v-col>

            <v-col cols="12" md="4" v-if="fields.obra_id && !hideFrentes">
              <v-autocomplete
                v-model="fields.frente_id"
                :items="frentes_obra"
                :item-text="getFrenteText"
                item-value="ID"
                label="Frente de Obra"
                outlined
                dense
                clearable
                :loading="loadingFrentes"
                @change="changeFrenteObra"
                @click:clear="clearFrente"
              />
            </v-col>

            <v-col cols="12" md="4" v-if="fields.obra_id && classeNeeded">
              <v-autocomplete
                v-model="fields.classe_id"
                :items="classes"
                :item-text="getClasseText"
                item-value="ID"
                label="Classe"
                outlined
                dense
                clearable
                :loading="loadingClasses"
              />
            </v-col>

            <v-col cols="12" md="3">
              <v-text-field
                v-model="fields.subcontract_cost"
                label="Custo Subempreitada"
                outlined
                dense
                type="text"
                inputmode="decimal"
                @keydown="onlyNumericOneDot"
                @input="sanitizeCost('subcontract_cost', $event)"
              />
            </v-col>

            <v-col cols="12" md="3">
              <v-text-field
                v-model="fields.material_cost"
                label="Custo Material"
                outlined
                dense
                type="text"
                inputmode="decimal"
                @keydown="onlyNumericOneDot"
                @input="sanitizeCost('material_cost', $event)"
              />
            </v-col>

            <v-col cols="12" md="3">
              <v-text-field
                v-model="fields.labor_cost"
                label="Custo Mão Obra"
                outlined
                dense
                type="text"
                inputmode="decimal"
                @keydown="onlyNumericOneDot"
                @input="sanitizeCost('labor_cost', $event)"
              />
            </v-col>

            <v-col cols="12" md="3">
              <v-text-field
                v-model="fields.machine_cost"
                label="Custo Máquinas"
                outlined
                dense
                type="text"
                inputmode="decimal"
                @keydown="onlyNumericOneDot"
                @input="sanitizeCost('machine_cost', $event)"
              />
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model="fields.notes"
                label="Notas"
                outlined
                dense
                rows="4"
                auto-grow
              />
            </v-col>
          </v-row>

          <v-divider class="my-6" />

          <v-row>
            <v-col cols="12" class="d-flex justify-end">
              <v-btn
                text
                class="mr-2"
                @click="$router.push('/ordens-trabalho')"
              >
                Voltar
              </v-btn>

              <v-btn
                color="primary"
                :loading="saving"
                :disabled="saving"
                @click="save"
              >
                <v-icon left>mdi-content-save</v-icon>
                Guardar
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-tab-item>

      <v-tab-item>
        <div
          v-if="!canUseDiariesTab"
          class="pa-6 text-center grey--text text--darken-1"
        >
          Guarda primeiro a ordem de trabalho para poderes consultar as diárias.
        </div>

        <div v-else>
          <v-data-table
            :expanded.sync="expanded"
            show-expand
            single-expand
            :headers="diaryHeaders"
            :loading="diariesLoading"
            :server-items-length="diariesTotal"
            :items="diaries"
            :footer-props="tableFooter"
            :options.sync="diaryOptions"
            class="elevation-1 diaries-table"
            :item-class="rowClass"
          >
            <template v-slot:item.responsavel="{ item }">
              <div class="d-none d-lg-block">
                <Tooltip
                  class="my-4 mr-4"
                  color="primary"
                  :text="item.approved_by"
                  top
                >
                  {{ item.responsavel || "—" }}
                </Tooltip>
              </div>

              <div class="d-md-none">
                {{ item.responsavel || "—" }}
                <span v-if="item.approved_by"> - {{ item.approved_by }}</span>
              </div>
            </template>

            <template v-slot:item.chave="{ item }">
              <Tooltip
                class="my-4 mr-4"
                color="primary"
                :text="item.work_name"
                top
              >
                {{ item.chave || "—" }}
              </Tooltip>
            </template>

            <template v-slot:item.creator="{ item }">
              <div class="d-none d-lg-block">
                <Tooltip
                  class="my-4 mr-4"
                  color="primary"
                  :text="item.creator_name"
                  top
                >
                  {{ item.creator || "—" }}
                </Tooltip>
              </div>

              <div class="d-md-none">
                {{ item.creator || "—" }}
                <span v-if="item.creator_name"> - {{ item.creator_name }}</span>
              </div>
            </template>

            <template v-slot:item.approval_status="{ item }">
              <v-row align="center" justify="center" no-gutters>
                <v-col cols="auto" class="mx-1">
                  <v-icon
                    small
                    color="warning"
                    v-if="item.creation_role === 'manobrador'"
                  >
                    mdi-steering
                  </v-icon>

                  <v-icon
                    small
                    color="black"
                    v-if="item.creation_role === 'encarregado'"
                  >
                    mdi-account-hard-hat
                  </v-icon>
                </v-col>

                <v-col cols="auto" class="mx-1">
                  <v-tooltip
                    top
                    v-if="item.primavera_integrated == '1' || item.primavera_integrated === 1"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        v-bind="attrs"
                        v-on="on"
                        color="success"
                      >
                        mdi-toy-brick-outline
                      </v-icon>
                    </template>

                    <span>Integrada no Primavera</span>
                  </v-tooltip>

                  <template v-else>
                    <v-icon
                      small
                      color="success"
                      v-if="checkApprovalStatus(item) === 'total'"
                    >
                      mdi-check-decagram
                    </v-icon>

                    <span v-else>{{ checkApprovalStatus(item) }}</span>
                  </template>
                </v-col>

                <v-col cols="auto" class="mx-1" v-if="item.weather != null">
                  <v-icon small>{{ getWeatherIcon(item.weather) }}</v-icon>
                </v-col>

                <v-col cols="auto" class="mx-1" v-if="item.parque_approved">
                  <v-icon small color="error">mdi-parking</v-icon>
                </v-col>

                <v-col cols="auto" class="mx-1" v-if="item.diary_kpi_header_id != null">
                  <v-icon color="blue darken-2">mdi-passport-biometric</v-icon>
                </v-col>

                <v-col cols="auto" class="mx-1" v-if="item.integration_error != null">
                  <v-icon color="error">mdi-sync-alert</v-icon>
                </v-col>
              </v-row>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-row justify="center" no-gutters>
                <v-col cols="auto">
                  <v-btn
                    fab
                    x-small
                    outlined
                    color="blue"
                    @click="openPreviewDiary(item.id)"
                  >
                    <v-icon>mdi-magnify</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </template>

            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="headers.length">
                <v-row>
                  <v-col cols="12" md="3" align="center">
                    <v-card class="elevation-0 transparent text-center">
                      <v-card-text class="text-center">
                        <span class="font-weight-bold text-body-1">
                          <v-icon color="warning" small class="mr-2">
                            mdi-clipboard-text-clock
                          </v-icon>
                          Criada a:
                        </span>
                        <br />
                        {{ item.created_at || "—" }}
                        <br />
                        <strong>({{ item.creator_name || "—" }})</strong>
                      </v-card-text>
                    </v-card>
                  </v-col>

                  <v-col cols="12" md="3">
                    <v-card class="elevation-0 transparent text-center">
                      <v-card-text class="text-center">
                        <span class="font-weight-bold text-body-1">
                          <v-icon color="warning" small class="mr-2">
                            mdi-swap-horizontal
                          </v-icon>
                          Modificada a:
                        </span>
                        <br />
                        {{ item.updated_at || "—" }}
                        <br />
                        <strong>({{ item.modified_by || "—" }})</strong>
                      </v-card-text>
                    </v-card>
                  </v-col>

                  <v-col cols="12" md="3">
                    <v-card class="elevation-0 transparent text-center">
                      <v-card-text class="text-center">
                        <span class="font-weight-bold text-body-1">
                          <v-icon color="warning" small class="mr-2">
                            mdi-list-status
                          </v-icon>
                          Aprovada a:
                        </span>
                        <br />
                        {{ item.approved_at || "—" }}
                        <br />
                        <strong>({{ item.approved_by || "—" }})</strong>
                      </v-card-text>
                    </v-card>
                  </v-col>

                  <v-col cols="12" md="3" align="end">
                    <v-card class="elevation-0 transparent text-end">
                      <v-card-text class="text-end">
                        <v-btn
                          v-if="item.versions"
                          block
                          class="primary mb-2"
                          rounded
                          x-small
                          @click="openHistorico(item)"
                        >
                          <v-icon class="mr-2" x-small>mdi-eye</v-icon>
                          Histórico edições
                        </v-btn>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </td>
            </template>

            <template v-slot:no-data>
              <div class="pa-6 grey--text text--darken-1">
                Sem diárias associadas a esta ordem de trabalho.
              </div>
            </template>
          </v-data-table>
        </div>
      </v-tab-item>
    </v-tabs-items>

    <v-dialog width="80%" height="80%" v-model="previewDiary.show">
      <v-card>
        <PreviewDiary
          v-if="previewDiary.show"
          :diary_id="previewDiary.id"
          @close="previewDiary.show = false"
        />
      </v-card>
    </v-dialog>

    <v-dialog v-model="historico.show" width="50%">
      <v-card>
        <v-card-title class="primary white--text">
          Histórico de edições
        </v-card-title>

        <v-card-text>
          <Historico
            v-if="historico.show && historico.item"
            :versions="historico.item.versions"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <ErrorSnackbar v-model="error.show" :message="error.message" />
    <SuccessSnackbar :message="success.message" v-model="success.show" />
  </div>
</template>

<script>
import OrdensTrabalho from "@/api/OrdensTrabalho.js";
import Obra from "@/api/Obra.js";
import FrenteObra from "@/api/FrenteObra.js";
import Classe from "@/api/Classe.js";
import Diaries from "@/api/Diaries.js";
import Tooltip from "@/components/UI/Tooltips/Tooltip";
import PreviewDiary from "@/views/Diaries/Preview/PreviewDiary.vue";
import Historico from "@/components/Diaries/Historico.vue";
import ErrorSnackbar from "@/components/UI/Snackbars/ErrorSnackbar";
import SuccessSnackbar from "@/components/UI/Snackbars/SuccessSnackbar";

export default {
  name: "OrdemTrabalho",

  components: {
    Tooltip,
    PreviewDiary,
    Historico,
    ErrorSnackbar,
    SuccessSnackbar,
  },

  props: {
    method: {
      type: String,
      required: true,
    },

    ordemTrabalho: {
      type: Object,
      default: null,
    },
  },

  data: () => ({
    activeTab: 0,

    valid: false,
    loading: false,
    saving: false,

    loadingObras: false,
    loadingFrentes: false,
    loadingClasses: false,

    obras: [],
    frentes_obra: [],
    classes: [],

    hideFrentes: false,
    classeNeeded: false,

    statusOptions: [
      { text: "Parada", value: "parada" },
      { text: "Concluída", value: "concluida" },
      { text: "Em andamento", value: "em_andamento" },
    ],

    fields: {
      name: null,
      status: "em_andamento",
      obra_id: null,
      frente_id: null,
      classe_id: null,
      notes: null,
      subcontract_cost: "0",
      material_cost: "0",
      labor_cost: "0",
      machine_cost: "0",
    },

    diariesLoading: false,
    diariesLoadedOnce: false,
    diaries: [],
    diariesTotal: 0,
    expanded: [],

    diaryOptions: {
      page: 1,
      itemsPerPage: 10,
      sortBy: [],
      sortDesc: [],
    },

    diaryHeaders: [
      {
        text: "Status",
        sortable: false,
        value: "approval_status",
        align: "center",
      },
      {
        text: "Obra",
        sortable: false,
        value: "chave",
        align: "center",
      },
      {
        text: "Data",
        sortable: false,
        value: "data",
        align: "center",
      },
      {
        text: "Resp. Aprovação",
        sortable: false,
        value: "responsavel",
        align: "center",
      },
      {
        text: "Criada Em",
        value: "created_at",
        sortable: false,
        align: "center",
      },
      {
        text: "Criada Por",
        sortable: false,
        value: "creator",
        align: "center",
      },
      {
        text: "Opções",
        value: "actions",
        align: "center",
        sortable: false,
      },
    ],

    tableFooter: {
      "items-per-page-options": [5, 10, 15, 20],
      "items-per-page-text": "Linhas por página",
    },

    previewDiary: {
      id: null,
      show: false,
    },

    historico: {
      item: null,
      show: false,
    },

    error: {
      show: false,
      message: null,
    },

    success: {
      show: false,
      message: null,
    },
  }),

  computed: {
    canUseDiariesTab() {
      return this.method === "update"
        && !!this.ordemTrabalho
        && !!this.ordemTrabalho.id
        && !!this.fields.obra_id;
    },

    totalCost() {
      return this.toNumber(this.fields.subcontract_cost)
        + this.toNumber(this.fields.material_cost)
        + this.toNumber(this.fields.labor_cost)
        + this.toNumber(this.fields.machine_cost);
    },

    formattedTotalCost() {
      return this.totalCost.toLocaleString("pt-PT", {
        style: "currency",
        currency: "EUR",
      });
    },
  },

  watch: {
    activeTab(value) {
      if (value === 1 && this.canUseDiariesTab && !this.diariesLoadedOnce) {
        this.searchDiaries();
      }
    },

    diaryOptions: {
      deep: true,
      handler() {
        if (this.activeTab !== 1 || !this.canUseDiariesTab) {
          return;
        }

        setTimeout(() => {
          this.searchDiaries();
        }, 10);
      },
    },
  },

  async mounted() {
    this.loading = true;

    await this.getObras();

    if (this.method === "update" && this.ordemTrabalho) {
      await this.fillFromOrdemTrabalho();
    }

    this.loading = false;
  },

  methods: {
    requiredRule(value) {
      return !!value || "Campo obrigatório";
    },

    toNumber(value) {
      const number = Number(String(value ?? "0").replace(",", "."));
      return Number.isNaN(number) ? 0 : number;
    },

    onlyNumericOneDot(e) {
      const ctrl = [
        "Backspace",
        "Delete",
        "ArrowLeft",
        "ArrowRight",
        "Home",
        "End",
        "Tab",
        "Enter",
      ];

      if (ctrl.includes(e.key) || e.ctrlKey || e.metaKey) {
        return;
      }

      const val = e.target.value || "";

      if (/^\d$/.test(e.key)) {
        return;
      }

      if (e.key === "." || e.key === ",") {
        if (val.includes(".") || val.includes(",")) {
          e.preventDefault();
        }

        return;
      }

      e.preventDefault();
    },

    sanitizeCost(field, value) {
      let s = String(value ?? "");

      s = s.replace(",", ".");
      s = s.replace(/[^\d.]/g, "");

      const i = s.indexOf(".");

      if (i !== -1) {
        s = s.slice(0, i + 1) + s.slice(i + 1).replace(/\./g, "");
      }

      if (s.startsWith(".")) {
        s = "0" + s;
      }

      this.$set(this.fields, field, s === "" || s === "." ? "0" : s);
    },

    async fillFromOrdemTrabalho() {
      this.fields = {
        name: this.ordemTrabalho.name,
        status: this.ordemTrabalho.status || "em_andamento",
        obra_id: this.ordemTrabalho.obra_id,
        frente_id: this.ordemTrabalho.frente_id,
        classe_id: this.ordemTrabalho.classe_id,
        notes: this.ordemTrabalho.notes,
        subcontract_cost: this.ordemTrabalho.subcontract_cost ?? "0",
        material_cost: this.ordemTrabalho.material_cost ?? "0",
        labor_cost: this.ordemTrabalho.labor_cost ?? "0",
        machine_cost: this.ordemTrabalho.machine_cost ?? "0",
      };

      if (this.fields.obra_id) {
        await this.loadWorkMetadataForUpdate();
      }
    },

    async loadWorkMetadataForUpdate() {
      await this.getFrenteObra();

      if (this.fields.frente_id) {
        await this.changeFrenteObra(false);
        return;
      }

      if (this.hideFrentes || this.frentes_obra.length === 0) {
        await this.classesPorObra(false);
      }
    },

    async getObras() {
      this.loadingObras = true;

      const data = {
        tipo: "O",
      };

      try {
        const resp = await Obra.all(data);
        this.obras = resp.data || [];
      } finally {
        this.loadingObras = false;
      }
    },

    async changeWork() {
      this.fields.frente_id = null;
      this.fields.classe_id = null;

      this.frentes_obra = [];
      this.classes = [];
      this.hideFrentes = false;
      this.classeNeeded = false;

      this.diaries = [];
      this.diariesTotal = 0;
      this.diariesLoadedOnce = false;
      this.expanded = [];

      if (!this.fields.obra_id) {
        return;
      }

      await this.getFrenteObra();

      if (this.frentes_obra.length === 0) {
        await this.classesPorObra();
      }

      if (this.frentes_obra.length === 1) {
        this.fields.frente_id = this.frentes_obra[0].ID;
        await this.changeFrenteObra();
      }

      if (this.activeTab === 1 && this.canUseDiariesTab) {
        this.searchDiaries();
      }
    },

    async getFrenteObra() {
      this.loadingFrentes = true;

      let obraPai = null;

      this.obras.forEach((obra) => {
        if (obra.Id === this.fields.obra_id) {
          obraPai = obra.Codigo;
        }
      });

      if (!obraPai) {
        this.loadingFrentes = false;
        return;
      }

      try {
        const resp = await FrenteObra.all(obraPai);
        this.frentes_obra = resp.data || [];
      } finally {
        this.loadingFrentes = false;
      }
    },

    async classesPorObra(clearSelectedClass = true) {
      this.loadingClasses = true;

      if (clearSelectedClass) {
        this.fields.classe_id = null;
      }

      try {
        const resp = await Classe.getClassesByObraPai(this.fields.obra_id);

        this.classes = resp.data || [];
        this.hideFrentes = true;
        this.classeNeeded = this.classes.length > 0;

        if (this.classes.length === 1 && clearSelectedClass) {
          this.fields.classe_id = this.classes[0].ID;
        }
      } finally {
        this.loadingClasses = false;
      }
    },

    async changeFrenteObra(clearSelectedClass = true) {
      this.loadingClasses = true;

      if (clearSelectedClass) {
        this.fields.classe_id = null;
      }

      let frente = null;

      this.frentes_obra.forEach((item) => {
        if (item.ID === this.fields.frente_id) {
          frente = item;
        }
      });

      if (!frente) {
        this.classes = [];
        this.classeNeeded = false;
        this.loadingClasses = false;
        return;
      }

      try {
        if (frente.classes && frente.classes.length > 0) {
          this.classes = frente.classes;
        } else {
          const data = {
            SubEmp: frente.SubEmp,
          };

          const resp = await Classe.getClasses(data);
          this.classes = resp.data || [];
        }

        this.classeNeeded = this.classes.length > 0;

        if (this.classes.length === 1 && clearSelectedClass) {
          this.fields.classe_id = this.classes[0].ID;
        }
      } finally {
        this.loadingClasses = false;
      }
    },

    clearFrente() {
      this.fields.frente_id = null;
      this.fields.classe_id = null;
      this.classes = [];
      this.classeNeeded = false;
    },

    getObraText(item) {
      if (!item) {
        return "";
      }

      return `${item.Codigo} - ${item.Descricao}`;
    },

    getFrenteText(item) {
      if (!item) {
        return "";
      }

      return this.joinCodeAndDescriptionWithoutDuplicate(
        item.SubEmp || item.subemp || "",
        item.Descricao || item.descricao || item.name || ""
      );
    },

    getClasseText(item) {
      if (!item) {
        return "";
      }

      return this.joinCodeAndDescriptionWithoutDuplicate(
        item.Classe || item.classe || "",
        item.Descricao || item.descricao || item.name || ""
      );
    },

    joinCodeAndDescriptionWithoutDuplicate(code, description) {
      const cleanCode = code ? String(code).trim() : "";
      const cleanDescription = description ? String(description).trim() : "";

      if (!cleanCode && !cleanDescription) {
        return "—";
      }

      if (!cleanCode) {
        return cleanDescription;
      }

      if (!cleanDescription) {
        return cleanCode;
      }

      const normalizedDescription = cleanDescription.toLowerCase();
      const normalizedCode = cleanCode.toLowerCase();

      if (
        normalizedDescription === normalizedCode ||
        normalizedDescription.startsWith(normalizedCode + " -") ||
        normalizedDescription.startsWith(normalizedCode + "-")
      ) {
        return cleanDescription;
      }

      return `${cleanCode} - ${cleanDescription}`;
    },

    buildPayload() {
      return {
        name: this.fields.name,
        status: this.fields.status,
        obra_id: this.fields.obra_id,
        frente_id: this.hideFrentes ? null : this.fields.frente_id,
        classe_id: this.fields.classe_id,
        notes: this.fields.notes,
        subcontract_cost: this.toNumber(this.fields.subcontract_cost),
        material_cost: this.toNumber(this.fields.material_cost),
        labor_cost: this.toNumber(this.fields.labor_cost),
        machine_cost: this.toNumber(this.fields.machine_cost),
      };
    },

    save() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.saving = true;

      const payload = this.buildPayload();

      const request = this.method === "create"
        ? OrdensTrabalho.create(payload)
        : OrdensTrabalho.update(this.ordemTrabalho.id, payload);

      request
        .then(({ data }) => {
          this.success.message = "Ordem de trabalho gravada com sucesso.";
          this.success.show = true;

          if (this.method === "create") {
            this.$router.push("/ordens-trabalho/" + data.id);
          } else {
            window.location.reload();
          }
        })
        .catch((err) => {
          this.error.message = err.response?.data?.message || "Erro ao gravar a ordem de trabalho.";
          this.error.show = true;
        })
        .finally(() => {
          this.saving = false;
        });
    },

    buildDiaryRequest() {
      return {
        page: this.diaryOptions.page || 1,
        limit: this.diaryOptions.itemsPerPage || 10,
        type: 'eq',
        work_id: this.fields.obra_id,
        ordem_trabalho_id: this.ordemTrabalho.id,
      };
    },

    searchDiaries(event) {
      if (!this.canUseDiariesTab) {
        return;
      }

      if (!!event && event.type === "submit") {
        this.diaryOptions.page = 1;
      }

      this.diariesLoading = true;

      const request = this.buildDiaryRequest();

      Diaries.search(request)
        .then((response) => {
          const data = response.data;

          this.diariesTotal = data.total || 0;

          if (this.diaryOptions.page > 1) {
            const totalPages = Math.ceil(
              this.diariesTotal / this.diaryOptions.itemsPerPage
            );

            if (this.diaryOptions.page > totalPages) {
              this.diaryOptions.page = 1;
            }
          }

          this.diaries = data.data || [];
          this.diariesLoadedOnce = true;
        })
        .catch((err) => {
          this.error.message = err.response?.data?.message || "Erro ao pesquisar diárias.";
          this.error.show = true;
        })
        .finally(() => {
          this.diariesLoading = false;
        });
    },

    openPreviewDiary(id) {
      this.previewDiary.id = id;
      this.previewDiary.show = false;
      this.previewDiary.show = true;
    },

    openHistorico(item) {
      this.historico.item = item;
      this.historico.show = true;
    },

    rowClass(item) {
      const classes = [];

      if (item.primavera_integrated == 1 || item.primavera_integrated === true) {
        classes.push("row-integrated");
      }

      const value = item.locked;

      if (value != "0") {
        classes.push("row-locked");
      }

      return classes.join(" ");
    },

    parseWeather(weather) {
      if (!weather) {
        return null;
      }

      try {
        return typeof weather === "string" ? JSON.parse(weather) : weather;
      } catch (e) {
        return null;
      }
    },

    getWeatherIcon(weather) {
      const meta = this.parseWeather(weather);

      if (!meta || !meta.day || !meta.day.condition) {
        return null;
      }

      const conditionText = meta.day.condition?.text || "";
      const lower = conditionText.toLowerCase();

      let icon = "mdi-weather-partly-cloudy";

      if (
        lower.includes("chuva") ||
        lower.includes("chuvisco") ||
        lower.includes("aguace")
      ) {
        icon = "mdi-weather-rainy";
      } else if (lower.includes("neve")) {
        icon = "mdi-weather-snowy";
      } else if (
        lower.includes("neblina") ||
        lower.includes("nevoeiro") ||
        lower.includes("nevoa")
      ) {
        icon = "mdi-weather-fog";
      } else if (
        lower.includes("sol") ||
        lower.includes("céu limpo") ||
        lower.includes("ceu limpo")
      ) {
        icon = "mdi-weather-sunny";
      } else if (
        lower.includes("nublado") ||
        lower.includes("encoberto")
      ) {
        icon = "mdi-weather-cloudy";
      }

      return icon;
    },

    checkApprovalStatus(item) {
      if (item.creation_role === "encarregado" && item.level_needed_to_approve === "3") {
        item.level_needed_to_approve = 2;

        if (item.current_approval != null) {
          if (item.current_approval.level === "1") {
            item.current_approval.level = 0;
          }

          if (item.current_approval.level === "2") {
            item.current_approval.level = 1;
          }
        }
      }

      if (item.current_approval == null) {
        return "0 / " + item.level_needed_to_approve;
      }

      if (item.current_approval.level >= item.level_needed_to_approve) {
        return "total";
      }

      return item.current_approval.level + " / " + item.level_needed_to_approve;
    },
  },
};
</script>

<style scoped>
.diaries-table :deep(thead th) {
  font-weight: 800;
  color: rgba(0, 0, 0, 0.75);
}

.row-locked {
  background-color: #f0f0f0 !important;
}

.row-integrated {
  background-color: #e6e6e6 !important;
}

.row-integrated td {
  opacity: 0.85;
}
</style>